import { isWithinRange } from "../../../../../views/LogisticPark/RegistroIngreso/components/AccessTypeForm/TransportContentForm/transportDateFormat";

export type AuthorizationSchedule = {
  from?: string | null;
  to?: string | null;
  fromHour?: string | null;
  toHour?: string | null;
  days?: number[] | null;
};

export type AuthorizationValidationResult = {
  isCurrentlyValid: boolean;
  isDateInRange: boolean;
  isDayEnabled: boolean;
  isTimeInRange: boolean;
  nextEnabledDay: string | null;
};

const DAY_NAMES_SHORT: Record<number, string> = {
  1: "Dom",
  2: "Lun",
  3: "Mar",
  4: "Mie",
  5: "Jue",
  6: "Vie",
  7: "Sab",
};

const DAY_NAMES_FULL: Record<number, string> = {
  1: "Domingo",
  2: "Lunes",
  3: "Martes",
  4: "Miércoles",
  5: "Jueves",
  6: "Viernes",
  7: "Sábado",
};

export const ALL_DAYS_ORDERED: number[] = [2, 3, 4, 5, 6, 7, 1];

export function getDayShortName(apiDay: number): string {
  return DAY_NAMES_SHORT[apiDay] || "";
}

export function formatDateDisplay(dateStr?: string | null): string {
  if (!dateStr) return "";
  // Las invitaciones traen la fecha en formato DATE_ATOM ("2026-08-24T15:54:44-03:00"): nos
  // quedamos con la parte de fecha, que ya viene expresada en la hora del edificio. Resolverla
  // con `new Date()` la pasaría a la hora local del browser y podría correr el día.
  const datePart = dateStr.includes("T") ? dateStr.slice(0, 10) : dateStr;
  const parts = datePart.split("-");
  if (parts.length !== 3) return dateStr;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

export function jsDayToApiDay(jsDay: number): number {
  return jsDay + 1;
}

function timeFromAtom(dateStr?: string | null): string | null {
  if (!dateStr || !dateStr.includes("T")) return null;
  const time = dateStr.slice(11, 16);
  return /^\d{2}:\d{2}$/.test(time) ? time : null;
}

/**
 * Normaliza la vigencia de una autorización a un único shape para la tarjeta.
 *
 * Las prevalidaciones ya vienen planas (`from` / `to` / `fromHour` / `toHour` / `days`), mientras
 * que las invitaciones traen `beginDatetime` / `endDatetime` más un `calendar` con los días y la
 * franja horaria. Se decide por el shape del objeto y no por `_source`, así sigue funcionando si el
 * backend empieza a mandar `calendar` en otros tipos de autorización.
 *
 * Para las invitaciones la vigencia completa —fecha y hora, de los dos extremos— sale de
 * `beginDatetime` / `endDatetime`. Del `calendar` se toman únicamente los días habilitados: sus
 * `fromTime` / `toTime` son una franja que se repite cada día, no los extremos del rango, y por eso
 * no entran en el bloque de Vigencia (ver `computeScheduleStatus`, que sí los aplica aparte).
 */
export function buildAuthorizationSchedule(auth: any): AuthorizationSchedule {
  if (!auth) return { from: null, to: null, fromHour: null, toHour: null, days: null };

  const calendar = auth.calendar;
  const from = auth.from || auth.beginDatetime || null;
  const to = auth.to || auth.endDatetime || null;

  return {
    from,
    to,
    fromHour: auth.fromHour || timeFromAtom(auth.beginDatetime) || calendar?.fromTime || null,
    toHour: auth.toHour || timeFromAtom(auth.endDatetime) || calendar?.toTime || null,
    days: auth.days || calendar?.days || null,
  };
}

/**
 * Estado de vigencia calculado en el front, para las autorizaciones cuyo `status` no lo indica.
 * Las prevalidaciones traen "valid" / "invalid" del backend y no pasan por acá.
 *
 * La vigencia se resuelve contra los extremos `beginDatetime` / `endDatetime` (vía sus timestamps,
 * que es la comparación exacta), y encima se aplican las restricciones del `calendar`: día de la
 * semana habilitado y franja horaria diaria. Cada restricción ausente se considera abierta, para no
 * invalidar una autorización por datos incompletos.
 */
export function computeScheduleStatus(
  auth: any,
  schedule: AuthorizationSchedule,
  now: Date = new Date()
): "valid" | "invalid" {
  const isDateInRange = isWithinRange(
    auth?.beginTimestamp,
    auth?.endTimestamp,
    schedule.from || "",
    schedule.to || "",
    now.getTime()
  );
  if (!isDateInRange) return "invalid";

  const days = schedule.days;
  if (days && days.length > 0 && !days.includes(jsDayToApiDay(now.getDay()))) return "invalid";

  // La franja diaria se toma solo del `calendar`: `schedule.fromHour`/`toHour` son las horas de los
  // extremos del rango, y compararlas contra la hora de hoy dejaría la autorización fuera de rango
  // todos los días antes de esa hora.
  const dailyFrom = auth?.calendar?.fromTime;
  const dailyTo = auth?.calendar?.toTime;
  const nowTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(
    2,
    "0"
  )}`;
  if (dailyFrom && nowTime < dailyFrom) return "invalid";
  if (dailyTo && nowTime > dailyTo) return "invalid";

  return "valid";
}

function formatDayMonth(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
}

export function findNextEnabledDay(now: Date, enabledDays?: number[] | null): string | null {
  if (!enabledDays || enabledDays.length === 0) return null;

  for (let offset = 1; offset <= 7; offset++) {
    const candidate = new Date(now.getTime());
    candidate.setDate(candidate.getDate() + offset);
    const candidateApiDay = jsDayToApiDay(candidate.getDay());

    if (enabledDays.includes(candidateApiDay)) {
      const dayName = DAY_NAMES_FULL[candidateApiDay];
      return `${dayName} ${formatDayMonth(candidate)}`;
    }
  }

  return null;
}

export function formatTimeDisplay(timeStr?: string | null): string {
  if (!timeStr) return "";

  const cleaned = timeStr.trim();
  const amPmMatch = cleaned.match(/\b(AM|PM)\b/i);
  const timePart = cleaned.replace(/\s*(AM|PM)\s*/i, "").trim();
  const parts = timePart.split(":");
  if (parts.length < 2) return timeStr;

  let hours = parseInt(parts[0], 10);
  const minutes = parts[1].padStart(2, "0");
  if (Number.isNaN(hours)) return timeStr;

  let period: string;
  if (amPmMatch) {
    period = amPmMatch[1].toUpperCase();
    if (hours === 0) hours = 12;
  } else {
    period = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    if (hours === 0) hours = 12;
  }

  return `${hours}:${minutes} ${period}`;
}
